import React, { forwardRef, } from 'react';
import { expandFontTheme, theme } from '@contrast-security-inc/design-system-foundations';
import { css, cx } from '@emotion/css';
import { PopoverContext } from './PopoverProvider';
import { MenuContext } from './MenuContext';
import mergeRefs from 'merge-refs';
const menuStyle = css `
  user-select: none;

  * {
    box-sizing: border-box;
  }

  /* content area within popover */
  div:nth-child(1) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;
const menuHeaderStyle = css `
  padding: 12px 16px;
  width: 100%;
  border-bottom: 1px solid ${theme.color.neutral40};
  display: block;
  color: ${theme.color.neutral80};
  ${expandFontTheme(theme.font.labelStrong)};
  text-transform: uppercase;
`;
export const Menu = forwardRef(({ children, className, testId, onKeyDown, ...rest }, ref) => {
    const { getMenuProps } = React.useContext(MenuContext);
    return (React.createElement("div", { ...rest, ...getMenuProps?.({ onKeyDown }), className: cx(menuStyle, className, 'ds-menu'), "data-testid": testId, ref: ref, role: "menu" }, Array.isArray(children)
        ? React.Children.map(children, (element, index) => 
        // Each MenuItem needs to know the index so that it can figure out which item to highlight or disabled
        React.isValidElement(element)
            ? React.cloneElement(element, {
                // @ts-ignore as know this element should have an index prop
                index,
            })
            : undefined)
        : children));
});
Menu.displayName = 'Menu';
export const MenuSectionHeader = forwardRef(({ children, className, testId, ...rest }, ref) => (React.createElement("div", { ...rest, role: "menuitem", "aria-hidden": true, "data-testid": testId, className: cx(menuHeaderStyle, className, 'ds-menu-header'), ref: ref }, children)));
MenuSectionHeader.displayName = 'MenuSectionHeader';
export const MenuTrigger = forwardRef(({ children, onClick, onKeyDown, className, disabled, ...rest }, ref) => {
    // May or not be in a popover menu
    const { popoverTriggerProps, popoverProps } = React.useContext(PopoverContext);
    const { getMenuTriggerProps } = React.useContext(MenuContext);
    const menuTriggerProps = getMenuTriggerProps?.();
    const props = {
        ...rest,
        ...menuTriggerProps,
        ...popoverTriggerProps,
        ref: mergeRefs(ref, popoverTriggerProps?.ref),
        onClick: !disabled
            ? (event) => {
                menuTriggerProps?.onClick?.();
                popoverTriggerProps?.onClick?.(event);
                onClick?.(event);
            }
            : undefined,
        onKeyDown: !disabled
            ? (event) => {
                menuTriggerProps?.onKeyDown?.(event);
                popoverTriggerProps?.onKeyDown?.(event);
                onKeyDown?.(event);
            }
            : undefined,
    };
    let triggerChild = null;
    if (typeof children === 'function') {
        triggerChild = children({ isOpen: popoverProps?.isOpen, onClose: popoverProps?.onClose, ...props });
    }
    if (React.isValidElement(children)) {
        triggerChild = children;
    }
    if (triggerChild) {
        return React.cloneElement(triggerChild, {
            ...props,
            className: cx(className, 'ds-menu-trigger'),
            disabled,
            isDisabled: disabled, // Support our own <Button element
        });
    }
    return null;
});
MenuTrigger.displayName = 'MenuTrigger';
